import BigNumber from 'bignumber.js';

import type { DecodedInput } from 'types/api/decodedInput';
import type { Transaction } from 'types/api/transaction';

import { persianDigits } from 'ui/home/saba/format';

export function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '';
}

// Keep every on-chain decimal, including a one-Wei fee, without converting to Number.
export function exactAmount(value: string | null | undefined, decimals = 0): string {
  if (!hasValue(value)) return '—';
  const amount = new BigNumber(value!);
  if (!amount.isFinite() || !Number.isInteger(decimals) || decimals < 0 || decimals > 255) return '—';
  return persianDigits(amount.shiftedBy(-decimals).toFormat());
}

export function transactionStatus(tx: Pick<Transaction, 'status' | 'block_number'>) {
  if (tx.status === 'ok') return { label: 'موفق', tone: 'success' as const };
  if (tx.status === 'error') return { label: 'ناموفق', tone: 'error' as const };
  if (tx.block_number === null) return { label: 'در انتظار تأیید', tone: 'pending' as const };
  return { label: 'در حال بررسی', tone: 'pending' as const };
}

export function transactionType(type: number): string {
  const names: Record<number, string> = { '0': 'Legacy', '1': 'EIP-2930', '2': 'EIP-1559', '3': 'EIP-4844', '4': 'EIP-7702' };
  return names[type] ? `${ persianDigits(String(type)) } (${ names[type] })` : persianDigits(String(type));
}

function projectTitle(data: DecodedInput | null | undefined): string | undefined {
  if (!data) return;

  const direct = data.parameters.find((parameter) => /^(?:project_?)?title$/i.test(parameter.name.replace(/^_/, '')));
  if (direct && typeof direct.value === 'string' && direct.value.trim()) return direct.value.trim();

  for (const parameter of data.parameters) {
    if (Array.isArray(parameter.value)) {
      const title = parameter.value[5];
      if (typeof title === 'string' && title.trim()) return title.trim();
    }
    if (parameter.value && typeof parameter.value === 'object' && !Array.isArray(parameter.value)) {
      const fields = parameter.value as Record<string, unknown>;
      const title = fields.title ?? fields.projectTitle ?? fields.project_title;
      if (typeof title === 'string' && title.trim()) return title.trim();
    }
  }
}

export function transactionMethodLabel(
  method: string | null | undefined,
  decodedInput?: DecodedInput | null,
): string | undefined {
  if (method === 'deployProjectToken') {
    const title = projectTitle(decodedInput);
    return title ? `توکنایز پروژه ${ title }` : 'توکنایز پروژه جدید';
  }
  return method || undefined;
}

export function transactionDisplayLabel(
  tx: Pick<Transaction, 'method' | 'decoded_input' | 'to' | 'created_contract'>,
): string {
  if (tx.method) return transactionMethodLabel(tx.method, tx.decoded_input)!;
  if (tx.created_contract) return 'ایجاد قرارداد';
  if (tx.to?.is_contract) return 'فراخوانی قرارداد';
  return 'انتقال';
}
